(function (root, factory) {
  const api = factory(root.OlympusConfiguratorRules);
  root.OlympusConfiguratorEngine = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (defaultRules) {
  'use strict';

  const CURRENT_GENERATION = 'olympus_r454b_4g';
  const STYLE_LABELS = {
    wall_mount: 'Wall Mount',
    ceiling_cassette: 'Ceiling Cassette',
    concealed_duct: 'Concealed Duct'
  };

  function signature(capacities) {
    return capacities.map(Number).sort((a, b) => a - b).join('|');
  }

  function failure(code, message, details) {
    return { valid: false, code, message, details: details || {} };
  }

  function rulesOrThrow(rules) {
    const value = rules || defaultRules;
    if (!value || value.policy !== 'FAIL_CLOSED_EXPLICIT_MATCHUPS') {
      throw new Error('Governed Olympus rules are missing or invalid.');
    }
    return value;
  }

  function liquidSize(pipe) {
    return String(pipe).split('x')[0];
  }

  function lineSetFor(rules, pipe, lengthFt, includeUnavailable) {
    return rules.line_sets.find((item) => item.pipe === pipe && item.length_ft === Number(lengthFt) && (includeUnavailable || item.available_for_sale));
  }

  function availableLineSets(pipe, rules) {
    const data = rulesOrThrow(rules);
    return data.line_sets.filter((item) => item.pipe === pipe && item.available_for_sale).sort((a, b) => a.length_ft - b.length_ft);
  }

  function validatePiping(zoneCount, lengths, rules) {
    const data = rulesOrThrow(rules);
    const limits = data.piping.multi_by_connected_zones[String(zoneCount)];
    if (!limits) return failure('UNSUPPORTED_ZONE_COUNT', 'No governed piping limit exists for this zone count.');
    if (!Array.isArray(lengths) || lengths.length !== zoneCount || lengths.some((value) => !Number.isFinite(Number(value)))) {
      return failure('INVALID_PIPING_INPUT', 'Every zone must have one numeric piping length.');
    }
    const normalized = lengths.map(Number);
    const belowMinimum = normalized.findIndex((value) => value < data.piping.minimum_run_ft);
    if (belowMinimum >= 0) return failure('BELOW_MINIMUM_RUN', `Zone ${belowMinimum + 1} is below the ${data.piping.minimum_run_ft} ft minimum run.`);
    const overIndividual = normalized.findIndex((value) => value > limits.individual_max_ft);
    if (overIndividual >= 0) return failure('INDIVIDUAL_RUN_EXCEEDED', `Zone ${overIndividual + 1} exceeds the ${limits.individual_max_ft} ft individual limit.`, { zone: overIndividual + 1, limit_ft: limits.individual_max_ft });
    const total = normalized.reduce((sum, value) => sum + value, 0);
    if (total > limits.aggregate_max_ft) return failure('AGGREGATE_RUN_EXCEEDED', `Selected piping totals ${total} ft, above the ${limits.aggregate_max_ft} ft system limit.`, { total_ft: total, limit_ft: limits.aggregate_max_ft });
    return { valid: true, total_ft: total, aggregate_limit_ft: limits.aggregate_max_ft, individual_limit_ft: limits.individual_max_ft, remaining_ft: limits.aggregate_max_ft - total };
  }

  function assignPorts(zones) {
    return zones.map((zone, index) => ({ ...zone, originalIndex: index }))
      .sort((a, b) => b.capacity_btu - a.capacity_btu || a.originalIndex - b.originalIndex)
      .map((zone, index) => ({ zone: zone.originalIndex + 1, port: String.fromCharCode(65 + index), capacity_btu: zone.capacity_btu }))
      .sort((a, b) => a.zone - b.zone);
  }

  function coalesceCartItems(items) {
    const quantities = new Map();
    items.forEach((item) => quantities.set(item.id, (quantities.get(item.id) || 0) + item.quantity));
    return [...quantities.entries()].map(([id, quantity]) => ({ id, quantity }));
  }

  function singleAdditionalRefrigerant(system, lengthFt, rules) {
    const excess = Math.max(0, Number(lengthFt) - rules.piping.single_standard_length_ft);
    return {
      required: excess > 0,
      estimated_oz: Number((excess * rules.piping.additional_refrigerant_oz_per_ft[liquidSize(system.pipe)]).toFixed(2)),
      basis: 'DOC-0333 pp36-37; installer must verify final charge'
    };
  }

  function multiAdditionalRefrigerant(condenser, zones, totalFt, rules) {
    const baseline = rules.piping.multi_standard_length_per_model_port_ft * condenser.model_port_count_for_charge;
    const excess = Math.max(0, totalFt - baseline);
    const liquidSizes = [...new Set(zones.map((zone) => liquidSize(zone.pipe)))];
    if (!excess) return { required: false, estimated_oz: 0, baseline_ft: baseline, professional_calculation_required: false };
    if (liquidSizes.length !== 1) {
      return { required: true, estimated_oz: null, baseline_ft: baseline, professional_calculation_required: true, reason: 'The manual does not provide a mixed-liquid-size allocation formula.' };
    }
    return {
      required: true,
      estimated_oz: Number((excess * rules.piping.additional_refrigerant_oz_per_ft[liquidSizes[0]]).toFixed(2)),
      baseline_ft: baseline,
      professional_calculation_required: true,
      reason: 'Manufacturer formula estimate; installer must verify final charge. Manual also states 17.6 oz when AHU units are used.'
    };
  }

  function validateSingle(selection, rules) {
    const data = rulesOrThrow(rules);
    if (selection && selection.generation && selection.generation !== CURRENT_GENERATION) return failure('GENERATION_MISMATCH', 'Only current R-454B Olympus equipment is allowed.');
    const system = data.single_zone.find((item) => item.capacity_btu === Number(selection.capacity_btu) && item.available_for_sale);
    if (!system) return failure('UNSUPPORTED_SINGLE_SYSTEM', 'No current sellable, documented single-zone system matches this capacity.');
    if (selection.style && selection.style !== 'wall_mount') return failure('UNSUPPORTED_SINGLE_STYLE', 'Current single-zone authority supports wall-mounted pairs only.');
    const lengthFt = Number(selection.line_set_length_ft);
    if (lengthFt > system.max_run_ft) return failure('INDIVIDUAL_RUN_EXCEEDED', `This system is limited to ${system.max_run_ft} ft.`);
    if (lengthFt < data.piping.minimum_run_ft) return failure('BELOW_MINIMUM_RUN', `This system requires at least ${data.piping.minimum_run_ft} ft of piping.`);
    const lineSet = lineSetFor(data, system.pipe, lengthFt, false);
    if (!lineSet) return failure('NO_SELLABLE_LINE_SET', `No current sellable ${lengthFt} ft traditional flare line set is available in ${system.pipe}.`, { pipe: system.pipe, length_ft: lengthFt });
    if (selection.line_set_sku && selection.line_set_sku !== lineSet.sku) return failure('INVALID_PIPE_SIZE_RELATIONSHIP', 'The selected line-set SKU does not match the required pipe size.');
    return {
      valid: true,
      mode: 'single_zone',
      system,
      line_set: lineSet,
      additional_refrigerant: singleAdditionalRefrigerant(system, lengthFt, data),
      cart_items: [{ id: system.variant_id, quantity: 1 }, { id: lineSet.variant_id, quantity: 1 }],
      total_price_usd: system.price_usd + lineSet.price_usd
    };
  }

  function validateMulti(selection, rules) {
    const data = rulesOrThrow(rules);
    const zones = Array.isArray(selection.zones) ? selection.zones : [];
    if (selection && selection.generation && selection.generation !== CURRENT_GENERATION) return failure('GENERATION_MISMATCH', 'Only current R-454B Olympus equipment is allowed.');
    if (zones.length < 2 || zones.length > 6) return failure('UNSUPPORTED_ZONE_COUNT', 'Olympus multi-zone configurations require two to six governed zones.');
    if (zones.some((zone) => Number(zone.capacity_btu) === 36000)) return failure('INDOOR_36K_BLOCKED', 'No current sellable Olympus 36K indoor unit exists.');

    const resolvedZones = [];
    for (let index = 0; index < zones.length; index += 1) {
      const zone = zones[index];
      const unit = data.indoor_units.find((item) => item.style === zone.style && item.capacity_btu === Number(zone.capacity_btu) && item.available_for_sale);
      if (!unit) return failure('UNSUPPORTED_INDOOR_UNIT', `Zone ${index + 1} does not match a current governed indoor unit.`, { zone: index + 1 });
      const lengthFt = Number(zone.line_set_length_ft);
      const lineSet = lineSetFor(data, unit.pipe, lengthFt, false);
      if (!lineSet) return failure('NO_SELLABLE_LINE_SET', `Zone ${index + 1} has no current sellable ${lengthFt} ft traditional flare line set in ${unit.pipe}.`, { zone: index + 1, pipe: unit.pipe, length_ft: lengthFt });
      if (zone.line_set_sku && zone.line_set_sku !== lineSet.sku) return failure('INVALID_PIPE_SIZE_RELATIONSHIP', `Zone ${index + 1} line-set SKU does not match the required pipe size.`, { zone: index + 1 });
      resolvedZones.push({ ...unit, line_set_length_ft: lengthFt, line_set: lineSet });
    }

    const matchSignature = signature(resolvedZones.map((zone) => zone.capacity_btu));
    let condensers = data.condensers.filter((item) => item.available_for_sale && zones.length >= item.min_zones && zones.length <= item.max_customer_zones && (data.approved_matchups[item.model] || []).includes(matchSignature));
    if (selection.condenser_model) condensers = condensers.filter((item) => item.model === selection.condenser_model);
    condensers.sort((a, b) => a.capacity_btu - b.capacity_btu);
    if (!condensers.length) return failure('UNAPPROVED_MATCHUP', 'No current sellable condenser has this exact manufacturer-approved indoor-capacity combination.', { signature: matchSignature });
    const condenser = condensers[0];
    const piping = validatePiping(zones.length, resolvedZones.map((zone) => zone.line_set_length_ft), data);
    if (!piping.valid) return piping;
    const ports = assignPorts(resolvedZones);
    const cartItems = [{ id: condenser.variant_id, quantity: 1 }];
    resolvedZones.forEach((zone) => cartItems.push({ id: zone.variant_id, quantity: 1 }, { id: zone.line_set.variant_id, quantity: 1 }));
    return {
      valid: true,
      mode: 'multi_zone',
      condenser,
      zones: resolvedZones,
      signature: matchSignature,
      piping,
      ports,
      additional_refrigerant: multiAdditionalRefrigerant(condenser, resolvedZones, piping.total_ft, data),
      cart_items: coalesceCartItems(cartItems),
      total_price_usd: condenser.price_usd + resolvedZones.reduce((sum, zone) => sum + zone.price_usd + zone.line_set.price_usd, 0)
    };
  }

  return Object.freeze({ CURRENT_GENERATION, STYLE_LABELS, signature, availableLineSets, validatePiping, assignPorts, validateSingle, validateMulti });
});
